import { Target, Hammer, Rocket, Smartphone, Zap, Map, Building, Search, Eye } from 'lucide-react';

// Type definitions
export interface ReleaseFeature {
    title: string;
    items: string[];
}

export interface ReleaseSection {
    id: string;
    title: string;
    icon: React.ComponentType<any>;
    color: 'blue' | 'green' | 'purple' | 'gray';
    features: ReleaseFeature[];
}

export interface ReleaseImpact {
    customers: string;
    contractors: string;
    overall: string;
}

export interface Release {
    version: string;
    title: string;
    date: string;
    type: 'major' | 'minor' | 'patch';
    summary: string;
    sections: ReleaseSection[];
    impact: ReleaseImpact;
}

export interface ReleaseNotesData {
    [key: string]: Release;
}

// Release data
export const releaseNotesData: ReleaseNotesData = {
    "2.1.0": {
        version: "2.1.0",
        title: "Expanded Construction Projects & Enhanced Interactive Maps",
        date: "2025-08-11",
        type: "minor",
        summary: "Major expansion of our construction project database with 46 new major cities across 24 states, bringing our total to over 27,000 public construction projects. Enhanced map functionality with dynamic search capabilities and improved mobile experience with alphabetical listing features.",
        sections: [
            {
                id: "expanded-coverage",
                title: "Expanded Construction Project Coverage",
                icon: Building,
                color: "blue",
                features: [
                    {
                        title: "Major Cities Added",
                        items: [
                            "Added construction projects for 46 major US cities including New York, Los Angeles, Chicago, Houston, Phoenix, Philadelphia, San Antonio, San Diego, San Francisco, and many more",
                            "Expanded coverage across 24 states from coast to coast",
                            "Now featuring over 27,000 public construction projects nationwide",
                            "Enhanced project discovery for contractors and businesses in major metropolitan areas",
                            "Comprehensive coverage of both ongoing and recently completed construction projects"
                        ]
                    },
                    {
                        title: "Geographic Expansion",
                        items: [
                            "Coast-to-coast coverage including California, Texas, New York, Florida, Illinois, and 19 additional states",
                            "Major metropolitan areas now fully represented with thousands of projects",
                            "Enhanced regional project discovery for better local business opportunities",
                            "Improved geographic diversity ensures nationwide contractor engagement"
                        ]
                    }
                ]
            },
            {
                id: "enhanced-maps",
                title: "Interactive Map Enhancements",
                icon: Map,
                color: "green",
                features: [
                    {
                        title: "Dynamic Map Search",
                        items: [
                            "Added 'Search This Area' button that appears when users zoom or pan the job map",
                            "Users can now explore different areas and discover new opportunities by manually triggering searches",
                            "Prevents unnecessary automatic queries while enabling on-demand exploration",
                            "Enhanced user control over map-based job discovery"
                        ]
                    },
                    {
                        title: "Improved Mobile Experience",
                        items: [
                            "Added alphabetical state and county listing alongside interactive maps",
                            "Desktop users see listings to the right of maps, mobile users see them below",
                            "Each state and county shows the exact count of available listings",
                            "Enhanced accessibility for users who prefer list-based navigation over map interaction"
                        ]
                    }
                ]
            },
            {
                id: "technical-improvements",
                title: "Technical Fixes & Performance",
                icon: Zap,
                color: "purple",
                features: [
                    {
                        title: "AI Integration Restoration",
                        items: [
                            "Fixed critical error in JobDescriptionForm where AI response generation stopped working",
                            "Restored full functionality to AI-powered job description assistance",
                            "Resolved compatibility issues with updated API helper utilities",
                            "Enhanced reliability of AI features across the platform"
                        ]
                    }
                ]
            }
        ],
        impact: {
            customers: "Customers now have access to a dramatically expanded database of construction projects spanning 46 major cities. Enhanced map functionality allows for better exploration of opportunities in different areas, while the new alphabetical listings provide an alternative, mobile-friendly way to browse projects by location.",
            contractors: "Contractors benefit from access to over 27,000 construction projects across 24 states, significantly expanding their potential business opportunities. The enhanced map search functionality allows for strategic exploration of new markets and geographic areas for business expansion.",
            overall: "This release significantly expands BidBro's coverage and utility as a comprehensive construction project discovery platform. With nearly triple the geographic coverage and enhanced user experience features, the platform now serves as an essential tool for construction industry professionals nationwide."
        }
    },
    "2.0.0": {
        version: "2.0.0",
        title: "Construction Project Directory & Interactive Maps",
        date: "2025-07-24",
        type: "major",
        summary: "Revolutionary launch of a comprehensive construction project directory featuring thousands of projects, interactive state and county maps, enhanced project details, and a completely redesigned browsing experience. This major release transforms BidBro into both a job creation platform and a comprehensive construction project discovery tool.",
        sections: [
            {
                id: "public-directory",
                title: "Construction Project Directory",
                icon: Building,
                color: "blue",
                features: [
                    {
                        title: "Comprehensive Project Listings",
                        items: [
                            "Browse thousands of construction projects from across the United States",
                            "Projects organized by state, city, and county for easy discovery",
                            "Professional project cards with images, descriptions, and key details",
                            "Real-time project status tracking (In Progress, Complete)",
                            "Project value information displayed prominently for transparency"
                        ]
                    },
                    {
                        title: "Advanced Search & Filtering",
                        items: [
                            "Search projects by location, category, and project type",
                            "Filter by project categories (HVAC, Electrical, Plumbing, General Construction, etc.)",
                            "Location-based browsing with city and county-specific pages",
                            "Category-specific filtering within geographic areas"
                        ]
                    },
                    {
                        title: "Rich Project Details",
                        items: [
                            "High-quality street view images automatically captured for each project",
                            "Detailed project descriptions with AI-enhanced summaries",
                            "Property information including lot size, building area, and year built",
                            "Address details and geographic coordinates for precise location data"
                        ]
                    }
                ]
            },
            {
                id: "interactive-maps",
                title: "Interactive Mapping Experience",
                icon: Map,
                color: "green",
                features: [
                    {
                        title: "National Project Overview",
                        items: [
                            "Interactive United States map showing project counts by state",
                            "Hover effects displaying real-time project statistics",
                            "Click-through navigation to explore individual states",
                            "Visual representation of construction activity across the country"
                        ]
                    },
                    {
                        title: "State & County Drill-Down",
                        items: [
                            "Detailed state maps showing project distribution by county",
                            "County-level statistics and project counts",
                            "Seamless navigation from national to state to local levels",
                            "Mobile-optimized map interactions with responsive design"
                        ]
                    },
                    {
                        title: "Split-Screen Map Integration",
                        items: [
                            "Dual-pane interface showing project list and map simultaneously",
                            "Synchronized highlighting between map markers and project cards",
                            "Hover interactions showing project details directly on the map",
                            "Adjustable panel sizing for optimal viewing on any device"
                        ]
                    }
                ]
            },
            {
                id: "enhanced-browsing",
                title: "Enhanced Browsing Experience",
                icon: Eye,
                color: "purple",
                features: [
                    {
                        title: "Professional Project Cards",
                        items: [
                            "Image carousels showcasing multiple project photos",
                            "Clean, professional design with consistent formatting",
                            "Quick access to project details and location information",
                            "Category badges for easy project type identification"
                        ]
                    },
                    {
                        title: "Intelligent Navigation",
                        items: [
                            "SEO-optimized URLs for better search engine visibility",
                            "Breadcrumb navigation for easy location tracking",
                            "Direct links to project details with shareable URLs",
                            "Cross-linking between related projects and locations"
                        ]
                    },
                    {
                        title: "Mobile-First Design",
                        items: [
                            "Fully responsive design optimized for mobile browsing",
                            "Touch-friendly map interactions and navigation",
                            "Adaptive layouts that work seamlessly across all device sizes",
                            "Fast loading times for optimal mobile performance"
                        ]
                    }
                ]
            },
            {
                id: "technical-architecture",
                title: "Technical Architecture Improvements",
                icon: Rocket,
                color: "gray",
                features: [
                    {
                        title: "Database Migration & Performance",
                        items: [
                            "Database upgrade for improved performance and scalability",
                            "Server-side caching implementation for faster page loads",
                            "Optimized database queries for handling thousands of projects",
                            "Enhanced data integrity and backup capabilities"
                        ]
                    },
                    {
                        title: "Automated Content Generation",
                        items: [
                            "AI-powered project title and description generation",
                            "Automated street view image collection for all projects",
                            "Smart property data integration from multiple sources",
                            "Automated categorization of construction project types"
                        ]
                    },
                    {
                        title: "Scalable Infrastructure",
                        items: [
                            "Robust backend API supporting high-volume project data",
                            "Efficient image storage and delivery system",
                            "Automated data processing pipelines for new projects",
                            "Enterprise-grade database architecture for future growth"
                        ]
                    }
                ]
            }
        ],
        impact: {
            customers: "Customers now have access to a comprehensive directory of construction projects for research and inspiration, can easily find contractors working on similar projects in their area, and benefit from a more professional browsing experience with rich project details and visual content.",
            contractors: "Contractors gain visibility through a public directory showcasing their work areas and project types, can research local market activity and competition, and benefit from increased platform traffic driving more potential customer inquiries.",
            overall: "This release establishes BidBro as the definitive platform for construction project discovery and contractor matching, combining the ease of job creation with comprehensive market intelligence and professional project showcasing capabilities."
        }
    },
    "1.5.0": {
        version: "1.5.0",
        title: "Enhanced Performance, Security & Professional Features",
        date: "2025-06-20",
        type: "major",
        summary: "Major performance improvements with drastically-improved page load times, enhanced security with reCAPTCHA protection, professional job submission roles, improved mobile experience, and comprehensive SEO optimization for better discoverability.",
        sections: [
            {
                id: "performance-revolution",
                title: "Performance Improvements",
                icon: Zap,
                color: "blue",
                features: [
                    {
                        title: "Significantly Improved Page Loading",
                        items: [
                            "Completely rebuilt contractor search engine delivers dramatically faster results",
                            "Contractor listing pages now load 5x faster with continued optimizations planned",
                            "Optimized database queries reduce loading delays across the platform",
                            "Smart location-based filtering provides much faster results for user's area"
                        ]
                    },
                    {
                        title: "Enhanced Search & Discovery",
                        items: [
                            "Intelligent contractor matching based on precise location coordinates",
                            "Improved location detection with automatic city/state recognition",
                            "Better contractor availability filtering shows only active professionals",
                            "Enhanced distance-based matching for more relevant results"
                        ]
                    }
                ]
            },
            {
                id: "security-features",
                title: "Security & Professional Features",
                icon: Target,
                color: "green",
                features: [
                    {
                        title: "Advanced Spam Protection",
                        items: [
                            "Google reCAPTCHA v3 integration prevents automated spam submissions",
                            "Intelligent form validation protects against malicious requests",
                            "Enhanced contact form security with comprehensive field validation",
                            "Automatic request categorization for better customer service routing"
                        ]
                    },
                    {
                        title: "Professional Job Submission Roles",
                        items: [
                            "New role selection system: Homeowner, Property Manager, General Contractor, Business Owner",
                            "Tailored submission experience based on user type and project scale",
                            "Professional verification options for business accounts",
                            "Enhanced credibility indicators for different user types"
                        ]
                    }
                ]
            },
            {
                id: "mobile-seo",
                title: "Mobile Experience & SEO",
                icon: Smartphone,
                color: "purple",
                features: [
                    {
                        title: "Enhanced Mobile Experience",
                        items: [
                            "Completely redesigned mobile navigation with improved usability",
                            "Touch-optimized form interactions and button sizing",
                            "Faster mobile page loads with optimized resource loading",
                            "Better responsive design across all screen sizes and orientations"
                        ]
                    },
                    {
                        title: "Comprehensive SEO Optimization",
                        items: [
                            "Dynamic meta tags and structured data for better search engine visibility",
                            "Optimized page titles and descriptions for all contractor and location pages",
                            "Enhanced sitemap generation with automatic updates for new contractors",
                            "Better internal linking structure for improved search rankings"
                        ]
                    }
                ]
            },
            {
                id: "contact-system",
                title: "Professional Contact System",
                icon: Hammer,
                color: "purple",
                features: [
                    {
                        title: "Professional Contact Management",
                        items: [
                            "New ContactForm component with comprehensive field collection (name, email, phone, request type, details)",
                            "Integrated reCAPTCHA protection for spam prevention",
                            "Automatic request categorization (Investor Interest, Technical Support, General Questions)",
                            "Database storage for all customer requests with full audit trail"
                        ]
                    },
                    {
                        title: "Dual Notification System",
                        items: [
                            "Instant admin notifications for all contact requests with detailed information",
                            "Customer confirmation emails with request details and next steps",
                            "Restructured notification system with dedicated admin email templates",
                            "Enhanced email template organization for better maintainability"
                        ]
                    }
                ]
            },
            {
                id: "performance-optimizations",
                title: "Performance & Technical Improvements",
                icon: Zap,
                color: "gray",
                features: [
                    {
                        title: "Database & Backend Optimizations",
                        items: [
                            "Optimized MySQL queries reduce average page load times by 60%",
                            "Enhanced caching strategies for frequently accessed contractor data",
                            "Improved API response times with streamlined data processing",
                            "Better error handling and logging for improved platform reliability"
                        ]
                    },
                    {
                        title: "Code Quality & Maintainability",
                        items: [
                            "Comprehensive TypeScript integration across all new components",
                            "Enhanced component reusability and modular architecture",
                            "Improved development workflow with better tooling and automation",
                            "Enhanced monitoring and analytics for better user experience insights"
                        ]
                    }
                ]
            }
        ],
        impact: {
            customers: "Customers experience dramatically faster page loads, better mobile usability, enhanced security protection, and a more professional job submission process with role-based customization. The improved search functionality helps them find contractors more quickly and effectively.",
            contractors: "Contractors benefit from better visibility through SEO improvements, faster platform performance that reduces customer bounce rates, and enhanced professional presentation through improved profile pages and contact systems.",
            overall: "This release delivers enterprise-level performance and security while maintaining ease of use. The platform now handles high traffic volumes more efficiently and provides a more professional experience that builds trust with both customers and contractors."
        }
    },
    "1.4.0": {
        version: "1.4.0",
        title: "Streamlined Registration & Enhanced User Experience",
        date: "2025-06-10",
        type: "major",
        summary: "Revolutionary removal of customer registration requirements, enhanced job submission experience with improved image handling, and significant mobile experience improvements for better accessibility.",
        sections: [
            {
                id: "registration-removal",
                title: "Streamlined Access",
                icon: Target,
                color: "blue",
                features: [
                    {
                        title: "No Registration Required for Customers",
                        items: [
                            "Customers can now submit jobs immediately without creating accounts",
                            "Simplified onboarding process reduces barriers to platform entry",
                            "Email-based job management allows customers to track submissions easily",
                            "Optional account creation available for customers who want additional features"
                        ]
                    },
                    {
                        title: "Enhanced Job Submission Flow",
                        items: [
                            "Streamlined single-page job creation process with improved navigation",
                            "Better form validation provides immediate feedback and reduces submission errors",
                            "Enhanced progress indicators guide users through the submission process",
                            "Improved error handling with clear, actionable error messages"
                        ]
                    }
                ]
            },
            {
                id: "image-improvements",
                title: "Enhanced Image & Media Experience",
                icon: Rocket,
                color: "green",
                features: [
                    {
                        title: "Professional Image Handling",
                        items: [
                            "Automatic image optimization reduces load times while maintaining quality",
                            "Support for multiple image formats (JPG, PNG, WebP) with automatic conversion",
                            "Enhanced image preview functionality during job submission",
                            "Better image compression reduces bandwidth usage and improves mobile experience"
                        ]
                    },
                    {
                        title: "Improved Media Management",
                        items: [
                            "Drag-and-drop image upload with visual feedback",
                            "Better file size validation prevents upload errors",
                            "Enhanced image gallery displays for job details",
                            "Automatic thumbnail generation for faster page loading"
                        ]
                    }
                ]
            },
            {
                id: "mobile-enhancements",
                title: "Mobile Experience Improvements",
                icon: Smartphone,
                color: "purple",
                features: [
                    {
                        title: "Responsive Design Enhancements",
                        items: [
                            "Improved touch targets and button sizing for better mobile interaction",
                            "Enhanced form layouts optimized for mobile keyboards and input methods",
                            "Better text readability with improved font sizing and contrast",
                            "Streamlined navigation that works seamlessly across all device sizes"
                        ]
                    },
                    {
                        title: "Performance Optimizations",
                        items: [
                            "Faster mobile page loads through optimized resource loading",
                            "Reduced data usage with intelligent image compression",
                            "Better offline handling and network error recovery",
                            "Enhanced battery life through optimized JavaScript execution"
                        ]
                    }
                ]
            },
            {
                id: "contractor-matching",
                title: "Improved Contractor Experience",
                icon: Hammer,
                color: "gray",
                features: [
                    {
                        title: "Enhanced Matching & Notifications",
                        items: [
                            "Improved contractor matching algorithm provides more relevant job opportunities",
                            "Better email notifications with richer job details and customer information",
                            "Enhanced job invitation system with improved response tracking",
                            "Professional email templates that maintain consistent branding"
                        ]
                    },
                    {
                        title: "Streamlined Bid Process",
                        items: [
                            "Simplified bid submission forms with better field organization",
                            "Enhanced loading states provide clear feedback during bid submission",
                            "Improved bid confirmation process with detailed email receipts",
                            "Better integration with customer communication workflow"
                        ]
                    }
                ]
            }
        ],
        impact: {
            customers: "Customers can now access the platform immediately without registration barriers, experience faster and more intuitive job submission, and enjoy better mobile usability. The streamlined process reduces time-to-submission from minutes to seconds.",
            contractors: "Contractors will see increased job volume due to the removal of registration barriers for customers, receive higher quality job submissions with better images and details, and benefit from improved platform performance and reliability.",
            overall: "This release fundamentally transforms the user journey by removing friction points, enhancing mobile usability, and providing enterprise-level performance optimizations. The platform is now more accessible, faster, and provides better support for all users."
        }
    },
    "1.3.0": {
        version: "1.3.0",
        title: "Enhanced User Experience & Professional Communications",
        date: "2025-06-02",
        type: "major",
        summary: "Streamlined workflows, professional email communications, and enhanced contractor matching experience.",
        sections: [
            {
                id: "customer-experience",
                title: "Customer Experience Improvements",
                icon: Target,
                color: "blue",
                features: [
                    {
                        title: "Streamlined Job Creation Process",
                        items: [
                            "Enhanced Visual Feedback: New loading animations with the BidBro logo provide clear progress indicators during job submission",
                            "Improved Form Navigation: Sticky sidebar navigation in job creation for easier form completion on desktop",
                            "Better Category Selection: Expanded search box for easier contractor category selection"
                        ]
                    },
                    {
                        title: "Professional Email Communications",
                        items: [
                            "Redesigned Email Templates: All customer and contractor emails now feature consistent, professional styling that matches the BidBro brand",
                            "Enhanced Bid Notifications: When contractors submit bids, customers receive beautifully formatted emails with comprehensive bid details",
                            "Improved Job Confirmation: Job creation confirmation emails now include helpful next steps and matched contractor counts"
                        ]
                    }
                ]
            },
            {
                id: "contractor-experience",
                title: "Contractor Experience Enhancements",
                icon: Hammer,
                color: "green",
                features: [
                    {
                        title: "Streamlined Bid Submission",
                        items: [
                            "Enhanced Loading Experience: Professional loading animations during bid submission with real-time status updates",
                            "Improved Email Notifications: Contractors receive detailed confirmation emails when their bids are successfully submitted",
                            "Better Job Opportunity Emails: Enhanced job notification emails with comprehensive project details and customer information"
                        ]
                    },
                    {
                        title: "Simplified Contractor Matching",
                        items: [
                            "Faster Invitation Process: Improved contractor notification system with visual feedback when sending job invitations",
                            "Enhanced Professional Communications: All contractor emails now feature consistent, professional styling"
                        ]
                    }
                ]
            },
            {
                id: "technical-improvements",
                title: "Technical Improvements",
                icon: Rocket,
                color: "purple",
                features: [
                    {
                        title: "Better Performance & Reliability",
                        items: [
                            "Improved Loading States: Consistent loading overlays across all major user actions",
                            "Enhanced Email Delivery: Consolidated email system for more reliable delivery of important notifications",
                            "Better Modal Experience: Fixed overlay positioning issues for users on mobile and scrolled content"
                        ]
                    },
                    {
                        title: "Enhanced Payment Security & Authentication",
                        items: [
                            "Stripe Financial Connections Integration: Incorporated Stripe Financial Connections for secure payment processing and bank account verification",
                            "Enhanced Security Protocols: Improved authentication systems protect user data and financial information",
                            "Better Error Handling: More informative error messages help users resolve issues quickly"
                        ]
                    }
                ]
            }
        ],
        impact: {
            customers: "Customers benefit from a more polished, professional experience with better visual feedback, clearer communications, and enhanced email notifications that keep them informed throughout the entire job process.",
            contractors: "Contractors experience improved bid submission workflows, professional email communications that enhance their brand image, and better job matching notifications that help them respond to opportunities more effectively.",
            overall: "This release elevates the overall platform experience by introducing professional-grade communications, enhanced visual feedback, and improved reliability that builds trust and confidence for all users."
        }
    }
};